import * as React from 'react';
import {
  Body1,
  Button,
  DrawerBody,
  DrawerFooter,
  Field,
  Label,
  makeStyles,
  MessageBar,
  MessageBarGroup,
  ProgressBar,
  shorthands,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import type { CountryEnum, SupportCaseCategoryEnum } from '@foundershub/api-client';
import { useCreateUserSupportCaseMutation, useMetadataQuery } from '@foundershub/api-client';
import { SelectField, TextareaField } from '@foundershub/components';
import { DrawerContext } from '../../components/FoundersHubUserOverlayDrawer';
import { SupportLinks } from '../../components/SupportLinks';
import { DrawerHeading } from '../../drawers/DrawerHeading';
import fileIcon from '../../media/file-icon.svg';
import trashCan from '../../media/trash-can.svg';
import type { DrawerNavigationOptionalProp } from '../../modules/drawer';
import { getErrorMessages, getFieldError, getValidationState } from '../../modules/errors';
import { useCountryDropdownOptions } from '../../modules/intl/countries';
import { dropdownOptions, loggedInSupportCategoryLabelSlugs, supportCategoryLabels } from '../../modules/intl/metadata';
import { useUserRequired } from '../../store';

export function UserSupportForm({ onBackButtonClick }: DrawerNavigationOptionalProp) {
  const user = useUserRequired();
  const intl = useIntl();
  const classes = useStyles();
  const maxCharacterCount = 2000;
  const fileInput = React.useRef<HTMLInputElement>(null);
  const { setShowDrawer, setChildren } = React.useContext(DrawerContext);
  const { data: metaData } = useMetadataQuery();
  const [accountId, setAccountId] = React.useState<string>(user.accounts[0].id);
  const countryOptions = useCountryDropdownOptions(metaData?.countries);
  const [header, setHeader] = React.useState<string>(intl.formatMessage(supportTicketTitleLabel));
  const [characterCount, setCharacterCount] = React.useState(0);
  const [country, setCountry] = React.useState<string>(user.accounts[0].country);
  const [category, setCategory] = React.useState<SupportCaseCategoryEnum | ''>('');
  const [description, setDescription] = React.useState<string>('');
  const [file, setFile] = React.useState<File | null>(null);
  const [startup, setStartup] = React.useState<string>(user.accounts[0].name);
  const [caseId, setCaseId] = React.useState<string>('');
  const [supportFormCreated, setSupportFormCreated] = React.useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadInProgress, setUploadInProgress] = React.useState(false);
  const [createUserCase, { data, isLoading, isSuccess, isError, error }] = useCreateUserSupportCaseMutation();
  const urlParams = new URLSearchParams(location.search);
  const fieldsTouched = !!category && !!description;
  const history = useHistory();
  React.useEffect(() => {
    if (isSuccess) {
      if (urlParams.has('convid')) {
        history.replace('/');
      }
      setSupportFormCreated(!!data);
      setCaseId(data?.case_number ?? '');
      setHeader(intl.formatMessage(thankYouTitleLabel));
    }
  }, [isLoading]);

  const onBrowseClick = () => {
    fileInput.current?.click();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setUploadInProgress(true);
      setFile(event.target.files[0]);
      // Simulate upload progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress === 100) {
          setUploadInProgress(false);
          clearInterval(interval);
        }
      }, 500);
    }
  };

  const deleteFile = () => {
    setFile(null);
    setUploadInProgress(false);
    setUploadProgress(0);
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    setCharacterCount(e.target.textLength);
  };

  const onClickCreate = () => {
    createUserCase({
      account_pk: accountId,
      category: category as SupportCaseCategoryEnum,
      description,
      file,
      user: user.id,
      title: category,
      account: accountId,
      country: country as CountryEnum,
      copilot_convid: '',
    });
  };

  function closeDrawer() {
    setShowDrawer(false);
    setChildren(null);
  }

  return (
    <>
      {isLoading ? (
        <Spinner size="small" className={classes.spinner} />
      ) : (
        <>
          <DrawerHeading title={header} closeDrawer={closeDrawer} onBackButtonClick={onBackButtonClick} />
          <DrawerBody className={classes.formFields}>
            <div className={classes.info}>
              {supportFormCreated ? (
                <>
                  <MessageBarGroup animate="both">
                    <MessageBar intent="success" role="alert">
                      <Text>
                        {intl.formatMessage(ticketNumberLabel)} {caseId ?? ''}
                      </Text>
                    </MessageBar>
                  </MessageBarGroup>
                  {file && data?.has_attachment === false ? (
                    <MessageBar intent="warning" role="alert">
                      <Text>{intl.formatMessage(attachmentUploadFailedLabel)}</Text>
                    </MessageBar>
                  ) : null}
                </>
              ) : null}
              {isError ? (
                <MessageBarGroup animate="both">
                  <MessageBar intent="error" role="alert">
                    <Text>
                      {getErrorMessages(error, intl).map((value, i) => (
                        <Text key={i}>{value}</Text>
                      ))}
                    </Text>
                  </MessageBar>
                </MessageBarGroup>
              ) : null}
            </div>
            <Label>{intl.formatMessage(nameLabel)}</Label>
            <Body1>
              {user.first_name} {user.last_name}
            </Body1>
            <Label>{intl.formatMessage(countryLabel)}</Label>
            <Body1>{country && countryOptions.find((option) => option.value === country)?.label}</Body1>
            <Label>{intl.formatMessage(contactEmail)}</Label>
            <Body1>{user.email}</Body1>
            {user.accounts.length > 1 && !supportFormCreated ? (
              <SelectField
                label={intl.formatMessage(startupLabel)}
                defaultValue={startup}
                placeholder={intl.formatMessage(startupPlaceholder)}
                validationMessage={getFieldError(error, 'startup_name', intl)}
                validationState={getValidationState(error, 'startup_name')}
                onChange={(event, data) => {
                  setAccountId(user.accounts.find((account) => account.name === data.value)?.id ?? '');
                  setCountry(
                    user.accounts.find((account: { name: string }) => account.name === data.value)?.country ?? ''
                  );
                  setStartup(data.value as string);
                }}
                required
                className={supportFormCreated ? classes.readOnlyField : ''}
                disabled={supportFormCreated}
              >
                {user.accounts.map((account) => (
                  <option key={account.id} value={account.name}>
                    {account.name}
                  </option>
                ))}
              </SelectField>
            ) : (
              <>
                <Label>{intl.formatMessage(startupLabel)}</Label>
                <Body1>{startup}</Body1>
              </>
            )}
            {supportFormCreated ? (
              <>
                <Label>{intl.formatMessage(categoryLabel)}</Label>
                <Body1>
                  {metaData
                    ? dropdownOptions(metaData.support_case_categories, supportCategoryLabels, intl.formatMessage).find(
                        (option) => option.value === category
                      )?.label
                    : ''}
                </Body1>
              </>
            ) : (
              <SelectField
                label={intl.formatMessage(categoryLabel)}
                placeholder={intl.formatMessage(categoryPlaceholder)}
                defaultValue={category}
                required
                validationMessage={getFieldError(error, 'category', intl)}
                validationState={getValidationState(error, 'category')}
                disabled={supportFormCreated}
                onChange={(event, data) => {
                  setCategory(data.value as SupportCaseCategoryEnum);
                }}
              >
                <option disabled value="">
                  {intl.formatMessage(categoryPlaceholder)}
                </option>
                {metaData
                  ? dropdownOptions(metaData.support_case_categories, supportCategoryLabels, intl.formatMessage)
                      .filter((option) => loggedInSupportCategoryLabelSlugs.includes(option.value))
                      .map(({ label, value }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))
                  : null}
              </SelectField>
            )}
            {supportFormCreated ? (
              <>
                <Label>{intl.formatMessage(descriptionLabel)}</Label>
                <Body1>{description}</Body1>
              </>
            ) : (
              <TextareaField
                label={intl.formatMessage(descriptionLabel)}
                defaultValue={description}
                required
                placeholder={intl.formatMessage(descriptionPlaceholder)}
                maxLength={maxCharacterCount}
                className={supportFormCreated ? classes.readOnlyField : ''}
                disabled={supportFormCreated}
                onChange={onDescriptionChange}
                validationMessage={getFieldError(error, 'description', intl)}
                validationState={getValidationState(error, 'description')}
                hint={`${characterCount}/${maxCharacterCount} ${intl.formatMessage(charactersAvailable)}`}
              />
            )}
            <Label>{intl.formatMessage(attachmentLabel)}</Label>
            {/* TODO: Replace <div /> below with <FileUpload /> component after QA */}
            <div>
              {!file ? (
                <>
                  {supportFormCreated ? (
                    <Body1 className={classes.disabled}>{intl.formatMessage(noAttachmentAdded)}</Body1>
                  ) : (
                    <div onClick={onBrowseClick} className={classes.browseArea}>
                      <Text>{intl.formatMessage(filesLabel)}</Text>
                    </div>
                  )}
                </>
              ) : (
                <div className={classes.uploadArea}>
                  {supportFormCreated ? (
                    <Text>
                      {data?.has_attachment === false ? intl.formatMessage(attachmentUploadFailedLabel) : file.name}
                    </Text>
                  ) : (
                    <>
                      <div className={classes.fileIcon}>
                        <img src={fileIcon} alt={intl.formatMessage(fileIconAltText)} />
                      </div>
                      <div className={classes.progressArea}>
                        <Text className={classes.marginTop}>{file.name}</Text>
                        <Text className={classes.marginLeft}>{(file.size / (1024 * 1024)).toFixed(2)}MB</Text>
                        <Field validationMessage={''} validationState="none" className={classes.marginTop}>
                          <ProgressBar max={100} value={uploadProgress} shape="rounded" />
                        </Field>
                      </div>
                      <button onClick={deleteFile} className={classes.deleteButton}>
                        <img src={trashCan} alt={intl.formatMessage(fileIconAltText)} />
                      </button>
                    </>
                  )}
                </div>
              )}
              <input type="file" ref={fileInput} className={classes.input} onChange={onFileChange} />
            </div>
            {supportFormCreated && <SupportLinks />}
          </DrawerBody>
          {!supportFormCreated && (
            <DrawerFooter className={classes.footer}>
              <Button
                appearance="primary"
                onClick={onClickCreate}
                disabled={isSuccess || isLoading || uploadInProgress || !fieldsTouched}
              >
                {intl.formatMessage(submitLabel)}
              </Button>
              <Button onClick={closeDrawer}>{intl.formatMessage(cancelLabel)}</Button>
            </DrawerFooter>
          )}
        </>
      )}
    </>
  );
}

const supportTicketTitleLabel = defineMessage({
  defaultMessage: 'Submit a Support Ticket',
  description: 'drawer title',
});

const thankYouTitleLabel = defineMessage({
  defaultMessage: 'Thank you!',
  description: 'drawer title',
});

const nameLabel = defineMessage({
  defaultMessage: 'Name',
  description: 'name label',
});

const countryLabel = defineMessage({
  defaultMessage: 'Country/Region',
  description: 'country/region label',
});

const contactEmail = defineMessage({
  defaultMessage: 'Contact Email',
  description: 'contact email label',
});

const ticketNumberLabel = defineMessage({
  defaultMessage: 'Your ticket number is',
  description: 'ticket number label',
});

const attachmentUploadFailedLabel = defineMessage({
  defaultMessage:
    'Your support case was created, but the file attachment could not be uploaded. Please try re-attaching your file.',
  description: 'warning shown when case created successfully but file upload failed',
});

const startupLabel = defineMessage({
  defaultMessage: 'Startup',
  description: 'startup label',
});

const categoryLabel = defineMessage({
  defaultMessage: 'Category',
  description: 'category label',
});

const descriptionLabel = defineMessage({
  defaultMessage: 'Description',
  description: 'description label',
});

const attachmentLabel = defineMessage({
  defaultMessage: 'Attachment',
  description: 'attachment label',
});

const submitLabel = defineMessage({
  defaultMessage: 'Submit',
  description: 'submit label',
});

const cancelLabel = defineMessage({
  defaultMessage: 'Cancel',
  description: 'cancel label',
});

const descriptionPlaceholder = defineMessage({
  defaultMessage: 'Describe your problem',
  description: 'description placeholder',
});

const categoryPlaceholder = defineMessage({
  defaultMessage: 'Select Support Category',
  description: 'support category placeholder',
});

const startupPlaceholder = defineMessage({
  defaultMessage: 'Select Startup',
  description: 'startup placeholder',
});

const filesLabel = defineMessage({
  defaultMessage: 'Browse your files',
  description: 'File upload label',
});

const charactersAvailable = defineMessage({
  defaultMessage: 'characters available',
  description: 'character counter label',
});

const noAttachmentAdded = defineMessage({
  defaultMessage: 'No attachment added',
  description: 'no attachment added placeholder',
});

const fileIconAltText = defineMessage({
  defaultMessage: 'file icon',
  description: 'file icon alt text',
});

const useStyles = makeStyles({
  readOnlyField: {
    '> span, > span > select': {
      ...shorthands.border(0),
      ...shorthands.padding(0, tokens.spacingHorizontalXXL),
      boxShadow: 'unset',
      cursor: 'default',
      paddingLeft: 0,
    },
    '> span > span > svg': {
      display: 'none', // Hide Dropdown button
    },
    '> span:active, > span:hover, > span:focus-within, >span::after': {
      ...shorthands.border('0'),
      borderBottomColor: 'transparent',
    },
    '> span > textarea': {
      paddingLeft: 0,
    },
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalL,
  },
  fileIcon: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('10px'),
    ...shorthands.padding(
      tokens.spacingHorizontalM,
      tokens.spacingHorizontalL,
      tokens.spacingHorizontalM,
      tokens.spacingHorizontalL
    ),
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    '> label': {
      fontWeight: tokens.fontWeightSemibold,
    },
    '> div > label': {
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  spinner: {
    display: 'flex', // Use flex layout
    justifyContent: 'center', // Center horizontally
    alignItems: 'center', // Center vertically
    height: '100vh', // Take up full viewport height
    width: '100%', // Take up full viewport width
  },
  uploadArea: {
    display: 'flex',
    flexDirection: 'row',
    '& > *': {
      ...shorthands.margin(0, tokens.spacingHorizontalM, 0, 0),
    },
  },
  deleteButton: {
    backgroundColor: tokens.colorTransparentBackground,
    cursor: 'pointer',
    ...shorthands.border(tokens.borderRadiusNone),
  },
  input: {
    display: 'none',
  },
  progressArea: {
    flexDirection: 'column',
    flexGrow: 1,
    fontSize: tokens.fontSizeBase200,
  },
  marginTop: {
    textOverflow: 'ellipsis',
    marginTop: tokens.spacingVerticalM,
  },
  marginLeft: {
    marginLeft: tokens.spacingHorizontalXL,
  },
  browseArea: {
    height: '100px',
    maxHeight: '100%',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    color: tokens.colorBrandBackground,
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
  },
  disabled: {
    color: tokens.colorNeutralForeground4,
  },
  footer: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
  },
});
